import { useState, useEffect, useRef } from 'react';
import Table from '../tablas/view';
import ProductSearchDropdown from './ProductSearchDropdown';
import ButtonBasic from '../../atoms/buttonBasic/view';
import './style.css';
import ExcelDownloadButton from '../ExcelDownloadButton/view';
import ExcelUploadButton from '../ExcelUploadButton/view';
import { matchProducts } from '../../../usecases/products-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';

const ArrayRelater = ({ array1, profile, handleData = undefined, onChange = undefined, hideFooter = false }) => {
  const [relatedData, setRelatedData] = useState([]);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const autoMatchedRef = useRef(false);

  const uid = profile?.uid;
  const cid = profile?.cid ?? null;

  // Inicialización de datos y auto-match único al montar / cambiar array1 inicial
  useEffect(() => {
    if (!array1 || array1.length === 0) return;

    // Inicializar los datos con los campos por defecto si no existen
    const initializedData = array1.map(item => ({
      ...item,
      relatedItem: item.relatedItem || null,
      matchStatus: item.matchStatus || 'pending'
    }));
    setRelatedData(initializedData);

    // Auto-match se ejecuta una sola vez al cargar los datos iniciales
    if (uid && !autoMatchedRef.current) {
      autoMatchedRef.current = true;
      runAutoMatch(initializedData);
    }
  }, [array1, uid]);

  async function runAutoMatch(currentData) {
    if (!currentData || currentData.length === 0 || !uid) return;
    setLoadingMatch(true);
    try {
      const matchPayload = currentData.map(item => ({
        idx: item?.idx,
        description: item?.description,
        sku: item?.sku || ""
      }));

      const matchRes = await matchProducts(uid, cid, matchPayload);
      const matches = matchRes || [];

      setRelatedData(prevData => {
        const updated = prevData.map(item => {
          if (item.relatedItem) return item;
          const match = matches.find(m => m.idx === item.idx);
          if (match && match.related_item) {
            return calculateItemTotals({
              ...item,
              relatedItem: match.related_item,
              matchStatus: match.match_status // 'auto' | 'suggested' | 'pending'
            });
          }
          return item;
        });

        onChange?.(updated);
        return updated;
      });
    } catch (error) {
      console.error("Error en auto-match de productos:", error);
    } finally {
      setLoadingMatch(false);
    }
  }

  function calculateItemTotals(item) {
    const updatedItem = { ...item };
    const subtotal = parseFloat(updatedItem.subtotal || 0);
    const tax = parseFloat(updatedItem.tax || 0);

    if (!updatedItem.total) {
      updatedItem.total = subtotal + tax;
    }

    updatedItem.unit_price = updatedItem.total / (parseFloat(updatedItem.quantity) || 1);
    return updatedItem;
  }

  const handleProductSelect = (selectedProduct, item) => {
    setRelatedData(prev => {
      const updated = prev.map((dataItem, index) => {
        const isMatch = (item.idx !== undefined && dataItem.idx === item.idx) ||
                        (dataItem.description === item.description && index === prev.indexOf(item)) ||
                        dataItem === item;
        if (isMatch) {
          return calculateItemTotals({
            ...dataItem,
            relatedItem: selectedProduct,
            matchStatus: selectedProduct ? 'manual' : 'pending'
          });
        }
        return dataItem;
      });
      onChange?.(updated);
      return updated;
    });
  };

  const handleCreateProduct = (item) => {
    dispatchCustomEvent("open-new-product", {
      name: item.description,
      buy_value: item.unit_price || item.price,
      sku: item.sku
    });
  };

  const handleExcelUpload = async (rows) => {
    let new_rows = rows.map((item) => {
      const quantity = parseFloat(item['Cantidad']) || 0;
      const tax = parseFloat(item['Valor impuesto']) || 0;
      const total = parseFloat(item['Total']) || 0;
      const price = quantity > 0 ? total / quantity : 0;

      return {
        idx: item.idx || Math.floor(Math.random() * 0xFFFFFF).toString(16).padStart(6, '0'),
        description: item['Descripción'] || item['description'] || '',
        quantity,
        unit_price: price,
        total,
        tax,
        taxRate: parseFloat(item['Tasa impuesto']) || 0,
        relatedItem: null,
        matchStatus: 'pending',
        sku: item.SKU || item.sku || item.Sku || '',
      };
    }).filter(item => (item.description || item.sku) && item.quantity >= 0);

    setRelatedData(new_rows);
    if (uid && new_rows.length > 0) {
      runAutoMatch(new_rows);
    } else {
      onChange?.(new_rows);
    }
  };

  const relatedCount = relatedData.filter(item => item.relatedItem !== null).length;
  const totalCount = relatedData.length;
  const progressPercent = totalCount > 0 ? (relatedCount / totalCount) * 100 : 0;

  return (
    <div className="array-relater-container">
      <div className="relater-header">
        <div className="relater-header-top">
          <div className="progress-info">
            <h3>Relacionar Productos</h3>
            <span>{relatedCount} de {totalCount} relacionados</span>
          </div>
          <div className="relater-actions">
            <ExcelDownloadButton data={relatedData}>Descargar Plantilla</ExcelDownloadButton>
            <ExcelUploadButton onUpload={handleExcelUpload}>Cargar Excel</ExcelUploadButton>
          </div>
        </div>
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: progressPercent === 100 ? 'var(--color-success)' : 'var(--color-primary)'
            }}
          />
        </div>
      </div>

      <div className='table-relater-content'>
        <Table
          data={relatedData}
          isLoading={loadingMatch}
          maxHeight="calc(48vh - 120px)"
          emptyMessage="No hay datos"
          headersList={["Descripción", "Cantidad", "Precio Unit.", "Total", "Vinculación"]}
          excludeList={["idx", "unit", "tax", "taxRate", "sku", "matchStatus", "subtotal"]}
          formats={{
            unit_price: { type: "money" },
            total: { type: "money" },
            relatedItem: {
              type: "custom",
              children: (item, idx) => (
                <div className='product-div-table'>
                  <ProductSearchDropdown
                    data={item}
                    profile={profile}
                    loading={loadingMatch}
                    onSelected={(selectedProduct) => handleProductSelect(selectedProduct, item)}
                    onCreate={() => handleCreateProduct(item)}
                  />
                </div>
              )
            },
          }}
          allowSearch={false}
        />
        {!hideFooter && totalCount > 0 && (
          <div className="relater-footer">
            <ButtonBasic
              disabled={relatedCount < totalCount}
              onClick={() => handleData?.(relatedData)}
              style={relatedCount === totalCount ? {
                backgroundColor: 'var(--color-success)',
                borderColor: 'var(--color-success)',
                color: 'white'
              } : {}}
            >
              Confirmar y Continuar
            </ButtonBasic>
          </div>
        )}
      </div>

    </div>
  );
};

export default ArrayRelater;
